import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AssetRecordType,
  type TLBookmarkShape,
  type TLEmbedShape,
} from "@tldraw/tlschema";
import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";
import { getEmbedDefinition } from "./canvas/engine/builtin/media";
import { isWebLink } from "../shared/links";

/** Edit native bookmark/embed destinations without changing any shared source asset in place. */
export function WebLinkControls({
  editor,
  shape,
}: {
  editor: Editor;
  shape: TLBookmarkShape | TLEmbedShape;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLFormElement>(null);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(shape.props.url);
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const interacting = useValue(
    "embed interaction",
    () => editor.getEditingShapeId() === shape.id,
    [editor, shape.id],
  );
  const editable = !editor.getIsReadonly() && !shape.isLocked;
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector("input")?.focus();
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !trigger.current?.contains(target) &&
        !panel.current?.contains(target)
      )
        setOpen(false);
    };
    window.addEventListener("pointerdown", outside, true);
    return () => window.removeEventListener("pointerdown", outside, true);
  }, [open]);
  const show = () => {
    const asset =
      shape.type === "bookmark" &&
      shape.props.assetId &&
      editor.getAsset(shape.props.assetId);
    setUrl(shape.props.url);
    setTitle(
      asset && asset.type === "bookmark"
        ? asset.props.title
        : String(shape.meta.calqueTitle ?? ""),
    );
    setError("");
    setOpen(true);
  };
  const save = () => {
    const normalized = url.trim();
    if (!isWebLink(normalized)) {
      setError("Use an http or https URL without embedded credentials.");
      return;
    }
    if (shape.type === "embed" && !getEmbedDefinition(normalized)) {
      setError(
        "This website cannot be embedded. Add it as a web link instead.",
      );
      return;
    }
    const current = editor.getShape(shape.id);
    if (!current || current.isLocked || editor.getIsReadonly()) return;
    editor.markHistoryStoppingPoint("Edit web link");
    if (shape.type === "bookmark") {
      const old = shape.props.assetId && editor.getAsset(shape.props.assetId);
      const asset = AssetRecordType.create({
        type: "bookmark",
        props: {
          src: normalized,
          title: title.trim().slice(0, 160) || new URL(normalized).hostname,
          description:
            old && old.type === "bookmark" && old.props.src === normalized
              ? old.props.description
              : "Open this website",
          image:
            old && old.type === "bookmark" && old.props.src === normalized
              ? old.props.image
              : "",
          favicon:
            old && old.type === "bookmark" && old.props.src === normalized
              ? old.props.favicon
              : "",
        },
      });
      editor.createAssets([asset]);
      editor.updateShape<TLBookmarkShape>({
        id: shape.id,
        type: "bookmark",
        props: { url: normalized, assetId: asset.id },
      });
    } else {
      editor.setEditingShape(null);
      editor.updateShape<TLEmbedShape>({
        id: shape.id,
        type: "embed",
        props: { url: normalized },
        meta: { ...shape.meta, calqueTitle: title.trim().slice(0, 160) },
      });
    }
    setOpen(false);
    editor.focus();
  };
  const rect = open ? trigger.current?.getBoundingClientRect() : null;
  return (
    <>
      <button
        ref={trigger}
        type="button"
        disabled={!editable}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? setOpen(false) : show())}
      >
        Edit link
      </button>
      {shape.type === "embed" && interacting && (
        <button
          type="button"
          onClick={() => {
            editor.setEditingShape(null);
            editor.focus();
          }}
        >
          Exit interaction
        </button>
      )}
      {open &&
        rect &&
        createPortal(
          <form
            ref={panel}
            id={id}
            role="dialog"
            aria-label="Edit web link"
            className="web-link-popover floating-surface"
            style={{
              left: Math.max(8, Math.min(window.innerWidth - 310, rect.left)),
              top: Math.max(
                8,
                Math.min(window.innerHeight - 210, rect.bottom + 6),
              ),
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Escape") {
                event.preventDefault();
                setOpen(false);
              }
            }}
            onSubmit={(event) => {
              event.preventDefault();
              save();
            }}
          >
            <strong className="calque-popover-title">
              {shape.type === "bookmark" ? "Web link" : "Embedded link"}
            </strong>
            {shape.type === "bookmark" && (
              <label>
                Title
                <input
                  value={title}
                  maxLength={160}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </label>
            )}
            <label>
              URL
              <input
                type="url"
                value={url}
                maxLength={8192}
                spellCheck={false}
                onChange={(event) => {
                  setUrl(event.target.value);
                  setError("");
                }}
              />
            </label>
            {error && <small role="alert">{error}</small>}
            <div className="rich-link-actions">
              <button
                className="calque-link-cancel"
                type="button"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button className="calque-link-primary" type="submit">
                Save
              </button>
            </div>
          </form>,
          document.body,
        )}
    </>
  );
}
